/**
 * The banks a payee in Malaysia can hold an account with.
 *
 * Typed free-hand until now, and the stored data shows exactly what that costs:
 * 34 distinct values for about 15 banks. Maybank appeared five ways (maybank,
 * May bank, Maybank, Maybank Berhad, Maybank Islamic), Public Bank four
 * including "publick bank", and CIMB and RHB each appeared twice differing only
 * by a trailing space. Four rows were not banks at all — they were the account
 * holder's name, typed into the wrong box.
 * (user 2026-10-22: "can the bank go for dropdown for all?" /
 *  "find all existing bank in malaysia and put up")
 *
 * Grouped the way a person thinks about them, alphabetical within each group so
 * the list is predictable. The picker is type-ahead, so "may" reaches Maybank in
 * three keystrokes and the grouping never gets in the way.
 *
 * NOT a closed list. The picker accepts a value that is not here, because the
 * alternative is an agent who cannot be paid: a new licence, a rename, or a
 * genuinely unusual payee would otherwise be a dead end. The list is there to
 * make the right answer the easy one, not to forbid the rest — and it keeps
 * every value already in the database displayable.
 */

export const BANK_GROUPS = {
    'Commercial banks': [
        'Affin Bank',
        'Alliance Bank Malaysia',
        'AmBank',
        'Bangkok Bank',
        'Bank of China (Malaysia)',
        'China Construction Bank (Malaysia)',
        'CIMB Bank',
        'Citibank',
        'Hong Leong Bank',
        'HSBC Bank Malaysia',
        'Industrial and Commercial Bank of China (Malaysia)',
        'Maybank',
        'MUFG Bank (Malaysia)',
        'OCBC Bank (Malaysia)',
        'Public Bank',
        'RHB Bank',
        'Standard Chartered Bank Malaysia',
        'Sumitomo Mitsui Banking Corporation Malaysia',
        'United Overseas Bank (Malaysia)',
    ],
    'Islamic banks': [
        'Affin Islamic Bank',
        'Al Rajhi Bank Malaysia',
        'Alliance Islamic Bank',
        'AmBank Islamic',
        'Bank Islam Malaysia',
        'Bank Muamalat Malaysia',
        'CIMB Islamic Bank',
        'Hong Leong Islamic Bank',
        'HSBC Amanah Malaysia',
        'Kuwait Finance House (Malaysia)',
        'Maybank Islamic',
        'MBSB Bank',
        'OCBC Al-Amin Bank',
        'Public Islamic Bank',
        'RHB Islamic Bank',
        'Standard Chartered Saadiq',
    ],
    'Development & savings banks': [
        'Agrobank',
        'Bank Pembangunan Malaysia',
        'Bank Rakyat',
        'Bank Simpanan Nasional (BSN)',
        'EXIM Bank Malaysia',
        'SME Bank',
    ],
    'Digital banks': [
        'AEON Bank',
        'Boost Bank',
        'GX Bank',
        'KAF Digital Bank',
        'Ryt Bank',
    ],
};

/** Flat list, in group order — what the picker renders. */
export const MALAYSIAN_BANKS = Object.values(BANK_GROUPS).flat();

/** Which heading a bank sits under, for the picker's group labels. */
const GROUP_OF = Object.entries(BANK_GROUPS).reduce((acc, [group, names]) => {
    names.forEach(n => { acc[n] = group; });
    return acc;
}, {});

export const bankGroupOf = (name) => GROUP_OF[name] || 'Other';

/**
 * Best-effort match of a free-typed value to a listed bank, for DISPLAY only —
 * it never rewrites what is stored. "maybank", "May bank" and "MAYBANK BERHAD"
 * all point at Maybank, so an old record lines up with the picker instead of
 * looking like a bank nobody has heard of.
 *
 * Returns null when nothing matches, which is the honest answer for the four
 * rows that hold a person's name.
 */
export function matchBank(raw) {
    const s = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!s) return null;

    const exact = MALAYSIAN_BANKS.find(b => b.toLowerCase() === s);
    if (exact) return exact;

    // Strip the noise words people add, then compare on letters alone, so
    // "HONGLEONG BANK" and "Hong Leong Bank" land on the same answer.
    const squash = (v) => v.toLowerCase().replace(/\b(berhad|bhd|malaysia|bank|banking)\b/g, '').replace(/[^a-z]/g, '');
    const key = squash(s);
    if (!key) return null;

    return MALAYSIAN_BANKS.find(b => squash(b) === key)
        || MALAYSIAN_BANKS.find(b => squash(b).startsWith(key) && key.length >= 4)
        || null;
}

export default MALAYSIAN_BANKS;
